import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, from, Observable, tap} from 'rxjs';
import {AsaxPropertiesService} from 'asax-properties';
import {catchError, map, switchMap} from 'rxjs/operators';
import {AsaxElasticService, buildQueryTermsEqual} from 'asax-elastic';
import { Location } from '@angular/common';

interface Limit {
  readonly from: number;
  readonly size: number;
}

const maximumWindowElasticResultSize = 10000;

@Injectable({
  providedIn: 'root'
})
export class AsaxDownloadingService {
  private observationsMap = new Map<string, boolean>();
  private projectsMap = new Map<string, boolean>();
  private publicationsMap = new Map<string, boolean>();

  private observationsSubject = new BehaviorSubject<string[]>([]);
  private projectsSubject = new BehaviorSubject<string[]>([]);
  private publicationsSubject = new BehaviorSubject<string[]>([]);

  //If these values are updated, should also be updated into share-container.component.ts from RH project
  public requestDataPathStart: string = 'AQ_REQUEST_DATA_START';
  public requestDataPathEnd: string = '&AQ_REQUEST_DATA_END=End_of_request_data';
  public aqPathWithParams: string = 'AQ_PATH_WITH_PARAMS';

  constructor(private readonly asaxPropertiesService: AsaxPropertiesService,
              private readonly asaxElasticService: AsaxElasticService,
              private readonly location: Location) {
  }

  addObservationsToBeDownloaded(observationIds: string | string[]): void {
    addIdsToBeDownloadedInMap(observationIds, this.observationsMap);
    const selectedIds = getIdsToBeDownloadedFromMap(this.observationsMap);

    this.observationsSubject.next(selectedIds);
  }

  removeObservationsToBeDownloaded(observationIds: string | string[]): void {
    removeIdsToBeDownloadedInMap(observationIds, this.observationsMap);
    const selectedIds = getIdsToBeDownloadedFromMap(this.observationsMap);

    this.observationsSubject.next(selectedIds);
  }

  isObservationSelectedToBeDownloaded(observationId: string): boolean {
    return !!this.observationsMap.get(observationId);
  }

  getObservationsToBeDownloaded(): Observable<any[]> {
    return this.observationsSubject.asObservable();
  }

  addProjectsToBeDownloaded(projectIds: string | string[]): void {
    addIdsToBeDownloadedInMap(projectIds, this.projectsMap);
    const selectedIds = getIdsToBeDownloadedFromMap(this.projectsMap);

    this.projectsSubject.next(selectedIds);
  }

  removeProjectsToBeDownloaded(projectIds: string | string[]): void {
    removeIdsToBeDownloadedInMap(projectIds, this.projectsMap);
    const selectedIds = getIdsToBeDownloadedFromMap(this.projectsMap);

    this.projectsSubject.next(selectedIds);
  }

  isProjectSelectedToBeDownloaded(projectId: string): boolean {
    return !!this.projectsMap.get(projectId);
  }

  getProjectsToBeDownloaded(): Observable<any[]> {
    return this.projectsSubject.asObservable();
  }

  addPublicationsToBeDownloaded(publicationIds: string | string[]): void {
    addIdsToBeDownloadedInMap(publicationIds, this.publicationsMap);
    const selectedIds = getIdsToBeDownloadedFromMap(this.publicationsMap);

    this.publicationsSubject.next(selectedIds);
  }

  removePublicationsToBeDownloaded(publicationIds: string | string[]): void {
    removeIdsToBeDownloadedInMap(publicationIds, this.publicationsMap);
    const selectedIds = getIdsToBeDownloadedFromMap(this.publicationsMap);

    this.publicationsSubject.next(selectedIds);
  }

  isPublicationSelectedToBeDownloaded(publicationId: string): boolean {
    return !!this.publicationsMap.get(publicationId);
  }

  getPublicationsToBeDownloaded(): Observable<any[]> {
    return this.publicationsSubject.asObservable();
  }

  buildRequestToDownloadFromObservation(datasetId : string): Observable<any> {
    return this.fetchSelectedData([datasetId], [], []).pipe(
      switchMap((selectedData) => this.prepareStorageDataAndBuildRequest(selectedData))
    );
  }

  buildRequestToDownloadFromProject(projectCode : string): Observable<any> {
    return this.fetchSelectedData([], [projectCode], []).pipe(
      switchMap((selectedData) => this.prepareStorageDataAndBuildRequest(selectedData))
    );
  }

  buildRequestToDownloadFromPublication(bibCode : string): Observable<any> {
    return this.fetchSelectedData([], [], [bibCode]).pipe(
      switchMap((selectedData) => this.prepareStorageDataAndBuildRequest(selectedData))
    );
  }

  buildRequestToDownload(): Observable<any> {
    const observationIds = getSelectedIdsFromMap(this.observationsMap);
    const projectIds = getSelectedIdsFromMap(this.projectsMap);
    const publicationsIds = getSelectedIdsFromMap(this.publicationsMap);

    return this.fetchSelectedData(observationIds, projectIds, publicationsIds).pipe(
      switchMap((selectedData) => this.prepareStorageDataAndBuildRequest(selectedData))
    );
  }

  prepareStorageDataAndBuildRequest(selectedData: any) {
    //Store selected data into the browser's session 
    //It will be used in RH in case of user wants to share the link via bookmarking

    // Convert the object to a JSON string
    const jsonData = JSON.stringify(selectedData);

    //Store request info and the selected options in AQ
    localStorage.setItem(this.requestDataPathStart, encodeURIComponent(jsonData));
    localStorage.setItem(this.aqPathWithParams, encodeURI(window.location.href.split(this.requestDataPathStart)[0]));

    return this.buildRequest(selectedData);
  }

  fetchSelectedData(observationIds, projectIds, publicationsIds): Observable<any> {
    return combineLatest([
      this.searchObservationsByIds(observationIds),
      this.searchObservationsByProjectIds(projectIds),
      this.searchObservationsByPublicationIds(publicationsIds)
    ]).pipe(
      map(([observationsFromObservations, observationsFromProjects, observationsFromPublications]) =>
        [...observationsFromObservations, ...observationsFromProjects, ...observationsFromPublications].flat()
      ),
      map((observations) => ({
        projectCodes: [...new Set<string>(observations.map(({project: {code}}) => code))],
        gouss: [...new Set<string>(observations.map(({gous, mous}) => gous || mous))],
        mouss: [...new Set<string>(observations.map(({mous}) => mous))],
        sources: [...new Set<string>(observations.map(({sourceName}) => sourceName)).values()]
      })),
      switchMap(({projectCodes, gouss, mouss, sources}) => {
        return this.asaxElasticService.searchByQuery({
          index: 'deliverables',
          body: {
            size: 0,
            query: {
              bool: {
                must: [
                  {bool: {should: [{terms: {"groupOusUid.keyword": gouss}}, {bool: {must_not: {exists: {field: "groupOusUid.keyword"}}}}]}},
                  {bool: {should: [{terms: {"projectCode.keyword": projectCodes}}, {bool: {must_not: {exists: {field: "projectCode.keyword"}}}}]}},
                  {bool: {should: [{terms: {"memberOusUid.keyword": mouss}}, {bool: {must_not: {exists: {field: "memberOusUid.keyword"}}}}]}},
                  {bool: {should: [{terms: {"sourceName.keyword": sources}}, {bool: {must_not: {exists: {field: "sourceName.keyword"}}}}]}}
                ]
              }
            },
            aggregations: {
              projectCodes: {terms: {field: "projectCode.keyword", size: maximumWindowElasticResultSize}},
              gouss: {terms: {field: "groupOusUid.keyword", size: maximumWindowElasticResultSize}},
              mouss: {terms: {field: "memberOusUid.keyword", size: maximumWindowElasticResultSize}},
              sources: {terms: {field: "sourceName.keyword", size: maximumWindowElasticResultSize}},
              collections: {terms: {field: "collectionName.keyword", size: maximumWindowElasticResultSize}},
              arrays: {terms: {field: "array.keyword", size: maximumWindowElasticResultSize}},
              mimeTypes: {terms: {field: "mimeType.keyword", size: maximumWindowElasticResultSize}},
              fileClasses: {terms: {field: "fileClass.keyword", size: maximumWindowElasticResultSize}}
            }
          }
        })
      }),
      map(({aggregations}) => {
        const options = {};

        for (const [aggregationKey, aggregationValue] of Object.entries(aggregations)) {
          options[aggregationKey] = (aggregationValue as any)?.buckets.map(({key}) => key);
        }

        return options;
      })
    );
  }

  public buildRequestFromPath(requestData: any) {
    // Parse Data into an object
    const selectedData = JSON.parse(decodeURIComponent(requestData));
    return this.buildRequest(selectedData);
  }

  private buildRequest(selectedData: any) {
    return this.asaxElasticService.searchByQuery({
      index: 'deliverables',
      body: {
        size: 0,
        query: {terms: {"groupOusUid.keyword": selectedData?.gouss}},
        aggregations: {
          projectCodes: {terms: {field: "projectCode.keyword", size: maximumWindowElasticResultSize}},
          gouss: {terms: {field: "groupOusUid.keyword", size: maximumWindowElasticResultSize}},
          mouss: {terms: {field: "memberOusUid.keyword", size: maximumWindowElasticResultSize}},
          sources: {terms: {field: "sourceName.keyword", size: maximumWindowElasticResultSize}},
          collections: {terms: {field: "collectionName.keyword", size: maximumWindowElasticResultSize}},
          arrays: {terms: {field: "array.keyword", size: maximumWindowElasticResultSize}},
          mimeTypes: {terms: {field: "mimeType.keyword", size: maximumWindowElasticResultSize}},
          fileClasses: {terms: {field: "fileClass.keyword", size: maximumWindowElasticResultSize}}
        }
      }
    }).pipe(
      map(({aggregations}) => {
        const options = {};

        for (const [aggregationKey, aggregationValue] of Object.entries(aggregations)) {
          options[aggregationKey] = (aggregationValue as any)?.buckets.map(({key}) => {
            const selectedOptions: string[] = selectedData[aggregationKey];
            const checked: boolean = selectedOptions?.includes(key);
            return {key, checked}
          });
        }

        return options;
      })
    );
  }

  public configureAqPath() {
    let pathWithoutServer = window.location.href.split('/aq/')[1];
    let aqParamsPath = pathWithoutServer.split('&' + this.requestDataPathStart)[0];
    
    this.location.go(aqParamsPath);
    return aqParamsPath;
  }

  searchObservationsByIds(ids: string[]): Observable<any[]> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('_id', idsChunk);

      const promiseElasticSearchChunk = this.asaxElasticService.searchByQuery({
        index: 'observations',
        body: {
          _source: ['sourceName', 'gous', 'mous', 'project.code'],
          size: limit.size,
          query: idsChunkDSLQuery
        }
      }).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return from(Promise.all(promises)).pipe(
      map((responses: any) => responses.map(({hits}) => hits.hits.map(({_source}) => ({..._source})))),
      map((rows: any) => rows.flat()),
      catchError(() => [])
    );
  }

  searchObservationsByProjectIds(ids: string[]): Observable<any[]> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('project.code', idsChunk);

      const promiseElasticSearchChunk = this.asaxElasticService.countByQuery({
        index: 'observations',
        body: {query: idsChunkDSLQuery}
      }).pipe(
        switchMap(({count}) => {
          return this.asaxElasticService.searchByQuery({
            index: 'observations',
            body: {
              _source: ['sourceName', 'gous', 'mous', 'project.code'],
              size: count, query: idsChunkDSLQuery
            }
          })
        })
      ).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return from(Promise.all(promises)).pipe(
      map((responses: any) => responses.map(({hits}) => hits.hits.map(({_source}) => ({..._source})))),
      map((rows: any) => rows.flat()),
      catchError(() => [])
    );
  }

  searchObservationsByPublicationIds(ids: string[]): Observable<any[]> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('publications.bibCode', idsChunk);

      const promiseElasticSearchChunk = this.asaxElasticService.countByQuery({
        index: 'observations',
        body: {query: {nested: {path: 'publications', query: idsChunkDSLQuery}}}
      }).pipe(
        switchMap(({count}) => {
          return this.asaxElasticService.searchByQuery({
            index: 'observations',
            body: {
              _source: ['sourceName', 'gous', 'mous', 'project.code'],
              size: count,
              query: {nested: {path: 'publications', query: idsChunkDSLQuery}}
            }
          })
        })
      ).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return from(Promise.all(promises)).pipe(
      map((responses: any) => responses.map(({hits}) => hits.hits.map(({_source}) => ({..._source})))),
      map((rows: any) => rows.flat()),
      catchError(() => [])
    );
  }
}

const addIdsToBeDownloadedInMap = (ids: string | string[], map: Map<string, boolean>): void => {
  const idsArray = [];
  idsArray.push(ids);
  idsArray.flat().forEach(id => map.set(id, true));
};

const removeIdsToBeDownloadedInMap = (ids: string | string[], map: Map<string, boolean>): void => {
  const idsArray = [];
  idsArray.push(ids);
  idsArray.flat().forEach(id => map.set(id, false));
};

const getIdsToBeDownloadedFromMap = (map: Map<string, boolean>): string[] => {
  return [...map.entries()].filter(([id, isSelected]) => isSelected).map(([id, isSelected]) => id);
};

const getLimits = (total: number, size: number): Limit[] => {
  const array = Array.from(new Array(Math.floor(total / size)), (val, index) => index + 1);
  const limits = array.map((value, index) => ({from: index * size, size}));
  limits.push({from: limits.length * size, size: total % size});
  return limits;
};

const getSelectedIdsFromMap = (map: Map<string, boolean>) => {
  return [...map.entries()].filter(([id, selected]) => !!selected).map(([id, selected]) => id);
}
